import type { ColumnFiltersState, SortingState } from '@tanstack/react-table'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { AlertCircle } from 'lucide-react'
import { memo, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks/use-translation'
import { DataTableContent } from './components/DataTableContent'
import { useTablePersistence } from './hooks/use-table-persistence'
import type { DataTableProps } from './types/data-table.types'

/**
 * Server-side DataTable component.
 *
 * This component handles pagination, filtering, sorting, and search on the server side,
 * providing better performance for large datasets by only loading the data needed
 * for the current page and applying filters at the API level.
 */
function DataTableComponent<TData, TValue>({
  cardClassName,
  tableContainerClassName,
  columns,
  data,
  tableId,
  initialPageSize: propInitialPageSize,
  searchKey,
  searchPlaceholder,
  pageSizeOptions,
  actionButton,
  title,
  description,
  showCard = true,
  isLoading = false,
  isError = false,
  errorMessage,
  pagination,
  onPaginationChange,
  onFiltersChange,
  onSortingChange,
  onSearchChange,
  searchValue,
  filters,
  sorting,
  onRowClick,
  isFetching: isTableFetching = false,
}: DataTableProps<TData, TValue>) {
  if (errorMessage) {
    console.error(errorMessage)
  }

  const t = useTranslation()

  const { columnVisibility, onColumnVisibilityChange, pageSize, setPageSize } = useTablePersistence(
    {
      tableId,
      defaultPageSize: propInitialPageSize ?? 20,
      defaultColumnVisibility: {},
    },
  )

  const handleColumnFiltersChange = useCallback(
    (updater: ((old: ColumnFiltersState) => ColumnFiltersState) | ColumnFiltersState) => {
      const newFilters = typeof updater === 'function' ? updater(filters) : updater
      onFiltersChange(newFilters)
    },
    [onFiltersChange, filters],
  )

  const handleSortingChange = useCallback(
    (updater: ((old: SortingState) => SortingState) | SortingState) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater
      onSortingChange(newSorting)
    },
    [onSortingChange, sorting],
  )

  const handleSearchChange = useCallback(
    (searchValue: string) => {
      onSearchChange(searchValue)
    },
    [onSearchChange],
  )

  const handlePaginationChange = useCallback(
    (
      updater:
        | ((old: { pageIndex: number; pageSize: number }) => {
            pageIndex: number
            pageSize: number
          })
        | { pageIndex: number; pageSize: number },
    ) => {
      const currentPagination = {
        pageIndex: pagination.page - 1,
        pageSize: pageSize,
      }
      const newPagination = typeof updater === 'function' ? updater(currentPagination) : updater

      if (newPagination.pageSize !== pageSize) {
        setPageSize(newPagination.pageSize)
      }

      onPaginationChange(newPagination)
    },
    [onPaginationChange, pagination, pageSize, setPageSize],
  )

  const tableConfig = useMemo(
    () => ({
      data,
      columns,
      onSortingChange: handleSortingChange,
      onColumnFiltersChange: handleColumnFiltersChange,
      onColumnVisibilityChange,
      getCoreRowModel: getCoreRowModel(),
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      pageCount: pagination.total_pages,
      onPaginationChange: handlePaginationChange,
      state: {
        pagination: {
          pageIndex: pagination.page - 1,
          pageSize: pageSize,
        },
        columnFilters: filters,
        sorting: sorting,
        columnVisibility: columnVisibility,
      },
    }),
    [
      data,
      columns,
      handleSortingChange,
      handleColumnFiltersChange,
      onColumnVisibilityChange,
      pagination,
      handlePaginationChange,
      filters,
      sorting,
      columnVisibility,
      pageSize,
    ],
  )

  const table = useReactTable(tableConfig)

  const tableContent = useMemo(
    () => (
      <DataTableContent
        table={table}
        tableContainerClassName={tableContainerClassName}
        columns={columns}
        searchKey={searchKey}
        searchPlaceholder={searchPlaceholder}
        pageSizeOptions={pageSizeOptions}
        onSearchChange={handleSearchChange}
        searchValue={searchValue}
        filters={filters}
        onFiltersChange={onFiltersChange}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={onColumnVisibilityChange}
        isFetching={isTableFetching}
        isLoading={isLoading}
        onRowClick={onRowClick}
        pagination={pagination}
      />
    ),
    [
      table,
      columns,
      onRowClick,
      searchKey,
      searchPlaceholder,
      pageSizeOptions,
      handleSearchChange,
      searchValue,
      filters,
      onFiltersChange,
      columnVisibility,
      onColumnVisibilityChange,
      isTableFetching,
      isLoading,
      pagination,
      tableContainerClassName,
    ],
  )

  const errorContent = useMemo(
    () => (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-12 h-12 flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-destructive" />
        </div>
        <div className="text-center space-y-2">
          <p className="font-medium text-lg text-destructive">{t('common.errorOccurred')}</p>
          <p className="text-sm text-muted-foreground max-w-md">
            {t('dataTable.errorDescription')}
          </p>
        </div>
      </div>
    ),
    [t],
  )

  const content = useMemo(() => {
    if (isError) return errorContent
    return tableContent
  }, [isError, errorContent, tableContent])

  if (showCard) {
    return (
      <Card className={cn('pb-1', cardClassName)}>
        {(title || description || actionButton) && (
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                {title && <CardTitle className="text-sm">{title}</CardTitle>}
                {description && (
                  <CardDescription className="mt-1 text-xs">{description}</CardDescription>
                )}
              </div>
              {actionButton && (
                <Button
                  onClick={actionButton.onClick}
                  {...actionButton}
                  className="text-xs"
                  size="sm"
                >
                  {actionButton.icon}
                  {actionButton.label}
                </Button>
              )}
            </div>
          </CardHeader>
        )}
        <CardContent className="px-0 pb-0">
          <div className="px-6">{content}</div>
        </CardContent>
      </Card>
    )
  }

  return content
}

export const DataTable = memo(DataTableComponent) as typeof DataTableComponent
